// Action keys that carry API call info interpreted by this middleware.
export const API_DATA = 'API_Data';
export const META_DATA = 'MetaData';
export const API_ROOT = `/api/`;

// Middleware that interprets actions with API_DATA info specified.
// Performs the call and promises when such actions are dispatched.
export default (save) => (next) => (action) => {
  const callAPI = action[API_DATA];
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  let { endpoint, params, method } = callAPI;
  const { types } = callAPI;

  if (!method) method = 'POST';

  function actionWith(data) {
    const finalAction = Object.assign({}, action, data);
    delete finalAction[API_DATA];
    return finalAction;
  }

  const [requestType, successType, failureType] = types;
  next(actionWith({ type: requestType, [META_DATA]: action[META_DATA] || {} }));

  return callApi(endpoint, params, method, save.dispatch).then(
    (response) =>
      next(
        actionWith({
          response,
          type: successType,
        })
      ),
    (error) =>
      next(
        actionWith({
          type: failureType,
          error: error.errorMessage || error.message || error.statusText || error.errors,
          validationErrors: error.validationMessages || '',
          status: error.status,
          prompts: error.confirmationPrompts,
          violations: error.violations,
        })
      )
  );
};

function callApi(endpoint, params, type = 'POST', dispatch = null) {
  const fullUrl = API_ROOT + endpoint;
  const contentType = !params.contentType && params.contentType !== false ? 'application/json' : params.contentType;
  const dataType = params.dataType || 'json';
  const data = params.requestBody || null;
  const requestBody = data ? (contentType === 'application/json' ? JSON.stringify(data) : data.file) : null;
  const processData = contentType === 'application/json';
  return apiRequest(type, fullUrl, requestBody, contentType, dataType, dispatch, processData);
}

let reauthentication = {};

function apiRequest(type, fullUrl, requestBody, contentType, dataType, dispatch, processData) {
  const headers = {
    Accept: 'application/json',
  };

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  return Promise.resolve(
    fetch(fullUrl, {
      method: type,
      cache: 'no-cache',
      body: requestBody,
      headers,
      credentials: 'include',
    })
  )
    .then((response) => {
      if (response.status === 401 && fullUrl !== API_ROOT + 'login') {
        // If authorisation failure and we are not yet re-authenticating then flag re-authentication in progress
        // and call login modal
        if (!reauthentication.inProgress) {
          reauthentication.inProgress = true;
          reauthentication.promise = new Promise((resolve) => {
            reauthentication.resolve = resolve;
            dispatch(AppAction.loginRedirect());
          });
        }

        // Wait for re-authentication to be complete, then perform the network request again and return results
        return reauthentication.promise.then(() => {
          return apiRequest(type, fullUrl, requestBody, contentType, dataType, dispatch, processData);
        });
      }

      return response;
    })
    .then((response) => {
      // Request successful, if authentication is in progress and this was a login attempt then remove authentication
      // in progress flag and resolve promise to cause waiting requests to be re-attempted
      if (reauthentication.inProgress && fullUrl === API_ROOT + 'login') {
        reauthentication.inProgress = false;
        reauthentication.resolve();
      }

      if (!response.ok) {
        return Promise.reject({
          response,
        });
      }

      if (response.status !== 204) {
        return response.json();
      }

      return null;
    })
    .then((json) => {
      return Object.assign({}, json);
    })
    .catch((error) => {
      console.error(error);

      if (error.response) {
        return error.response.json().then((json) => {
          return Promise.reject(json);
        });
      }

      return Promise.reject(new Error('Failed to contact services'));
    });
}
